import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
    "group/button inline-flex shrink-0 items-center justify-center rounded-full border border-transparent bg-clip-padding text-sm font-semibold tracking-wide whitespace-nowrap transition-all duration-200 outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground shadow-sm hover:bg-navy-800',
                gold: 'bg-gold-400 text-navy-950 shadow-sm hover:bg-gold-300',
                emerald: 'bg-emerald-700 text-cream-50 shadow-sm hover:bg-emerald-800',
                outline: 'border-border bg-card text-foreground hover:border-gold-400 hover:bg-cream-100 aria-expanded:bg-muted',
                outlineLight: 'border-cream-50/40 bg-transparent text-cream-50 hover:border-gold-300 hover:bg-cream-50/10',
                secondary: 'bg-secondary text-secondary-foreground hover:bg-emerald-800',
                ghost: 'hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground',
                destructive: 'bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20',
                link: 'rounded-none px-0 text-primary underline-offset-4 hover:underline'
            },
            size: {
                default: 'h-10 gap-2 px-5',
                xs: "h-7 gap-1 px-2.5 text-xs [&_svg:not([class*='size-'])]:size-3",
                sm: 'h-9 gap-1.5 px-4 text-[0.8125rem]',
                lg: 'h-12 gap-2 px-7 text-[0.9375rem]',
                icon: 'size-10',
                'icon-xs': "size-7 [&_svg:not([class*='size-'])]:size-3",
                'icon-sm': 'size-8',
                'icon-lg': 'size-12'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
)

function Button({
    className,
    variant = 'default',
    size = 'default',
    ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
    return (
        <ButtonPrimitive
            data-slot='button'
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        />
    )
}

export { Button, buttonVariants }
